import React, { useContext, useId } from "react";
import MainContext from "../../../Context/MainContext/MainContext";
import MUITypography from "@mui/material/Typography";
import MUIBox from "@mui/material/Box";
import MUIIconArrowDropUp from "@mui/icons-material/ArrowDropUp";
import MUIIconArrowDropDown from "@mui/icons-material/ArrowDropDown";
import MUIIconEmojiEvents from "@mui/icons-material/EmojiEvents";
import MUIAmberColor from "@mui/material/colors/amber";
import MUIYellowColor from "@mui/material/colors/yellow";
import MUIBlueGreyColor from "@mui/material/colors/blueGrey";
import MUIOrangeColor from "@mui/material/colors/orange";

import Format from "../../../Utility/Format";
import Widget from "../../Widget";

/**
 * Resolves tooltip and badge chrome from numeric Presidents Club rank (1 = gold, 2 = silver, 3 = bronze).
 *
 * @param {unknown} currentRankFromServer
 * @returns {"generic"|"gold"|"silver"|"bronze"}
 */
function resolvePresidentsClubBadgeAppearanceKindFromCurrentRank(
  currentRankFromServer,
) {
  if (currentRankFromServer === 1) {
    return "gold";
  }
  if (currentRankFromServer === 2) {
    return "silver";
  }
  if (currentRankFromServer === 3) {
    return "bronze";
  }
  return "generic";
}

/**
 * Top accent stripe for the Presidents Club tooltip. Ranks 1–3 use a static metal gradient; generic keeps the original accent bar.
 *
 * @param {object} props
 * @param {"bronze"|"generic"|"gold"|"silver"} props.currentRankAppearanceType
 * @returns {React.ReactElement}
 */
function PresidentsClubTooltipRankAccentStripe(props) {
  if (props.currentRankAppearanceType === "generic") {
    return (
      <MUIBox
        sx={{
          background: `linear-gradient(90deg, ${MUIAmberColor[600]}, #38bdf8, #c084fc, ${MUIAmberColor[600]})`,
          height: 3,
        }}
      />
    );
  }

  let accentStripeBackgroundImage = "";
  const lightModeSilverAccentStripeBackgroundImage =
    "linear-gradient(90deg, #475569 0%, #64748b 10%, #94a3b8 22%, #aebdcc 44%, #aebdcc 50%, #94a3b8 78%, #64748b 90%, #475569 100%)";

  if (props.currentRankAppearanceType === "gold") {
    accentStripeBackgroundImage =
      "linear-gradient(90deg, #b45309 0%, #d97706 10%, #f59e0b 20%, #fbbf24 30%, #fde047 40%, #fef08a 50%, #fde047 60%, #fbbf24 70%, #f59e0b 80%, #d97706 90%, #b45309 100%)";
  } else if (props.currentRankAppearanceType === "silver") {
    accentStripeBackgroundImage =
      "linear-gradient(90deg, #64748b 0%, #94a3b8 10%, #cbd5e1 20%, #e2e8f0 30%, #f1f5f9 40%, #f8fafc 50%, #f1f5f9 60%, #e2e8f0 70%, #cbd5e1 80%, #94a3b8 90%, #64748b 100%)";
  } else {
    accentStripeBackgroundImage =
      "linear-gradient(90deg, #c2410c 0%, #ea580c 10%, #f97316 20%, #fb923c 30%, #fdba74 40%, #fed7aa 50%, #fdba74 60%, #fb923c 70%, #f97316 80%, #ea580c 90%, #c2410c 100%)";
  }

  return (
    <MUIBox
      sx={{
        backgroundImage: (theme) =>
          theme.palette.mode === "light" &&
          props.currentRankAppearanceType === "silver"
            ? lightModeSilverAccentStripeBackgroundImage
            : accentStripeBackgroundImage,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
        height: 4,
      }}
    />
  );
}

/**
 * Human-readable copy for points remaining until the next rank, using server `nextRankDelta`.
 *
 * @param {unknown} nextRankDelta
 * @returns {string}
 */
function buildPointsUntilNextRankDisplayText(nextRankDelta) {
  if (!nextRankDelta) {
    return "Not available";
  }
  const numericPointsUntilNextRank = Number(nextRankDelta);
  if (Number.isNaN(numericPointsUntilNextRank)) {
    return "Not available";
  }
  if (numericPointsUntilNextRank <= 0) {
    return "No additional points needed for the next rank";
  }
  const formattedPoints = Format.number(numericPointsUntilNextRank, {
    decimalPlaceCount: 2,
    shouldRoundValueToDecimalPlacesBeforeFormatting: true,
  });
  return formattedPoints;
}

/**
 * Rich tooltip body for Presidents Club standing (shown on whole widget hover).
 *
 * @param {object} props
 * @param {string} props.currentRankNumberDisplayText
 * @param {string|null} props.currentRankPercentileDisplayText
 * @param {"bronze"|"generic"|"gold"|"silver"} props.currentRankAppearanceType
 * @param {string} props.pointsUntilNextRankDisplayText
 * @returns {React.ReactElement}
 */
function PresidentsClubStandingTooltipPanel(props) {
  let iconColor = MUIAmberColor[700];
  if (props.currentRankAppearanceType === "gold") {
    iconColor = MUIYellowColor[800];
  } else if (props.currentRankAppearanceType === "silver") {
    iconColor = MUIBlueGreyColor[400];
  } else if (props.currentRankAppearanceType === "bronze") {
    iconColor = MUIOrangeColor[900];
  }

  return (
    <MUIBox
      sx={{
        background: (theme) => theme.palette.background.paper,
        borderRadius: 1,
        boxShadow: 4,
        maxWidth: 280,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <PresidentsClubTooltipRankAccentStripe
        currentRankAppearanceType={props.currentRankAppearanceType}
      />
      <MUIBox sx={{ padding: (theme) => theme.spacing(1.75, 2, 2, 2) }}>
        <MUIBox
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 1,
            marginBottom: 1,
          }}
        >
          <MUIIconEmojiEvents
            sx={{
              color: iconColor,
              display: "block",
              flexShrink: 0,
              fontSize: 30,
            }}
          />
          <MUIBox sx={{ flex: 1, minWidth: 0 }}>
            <MUITypography
              sx={{
                color: (theme) => theme.palette.text.secondary,
                fontSize: "0.65rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                lineHeight: 1.2,
                textTransform: "uppercase",
              }}
            >
              Presidents Club
            </MUITypography>
            <MUITypography
              sx={{
                color: (theme) => theme.palette.text.primary,
                fontSize: "0.95rem",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
              }}
            >
              Standing details
            </MUITypography>
          </MUIBox>
        </MUIBox>
        <MUIBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          <MUIBox
            sx={{
              alignItems: "baseline",
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            }}
          ></MUIBox>
          <MUIBox
            sx={{
              alignItems: "baseline",
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <MUITypography
              sx={{
                color: (theme) => theme.palette.text.secondary,
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              Currently in the top
            </MUITypography>
            <MUITypography
              sx={{
                color: (theme) => theme.palette.text.primary,
                fontSize: "0.85rem",
                fontVariantNumeric: "tabular-nums",
                fontWeight: 700,
              }}
            >
              {props.currentRankPercentileDisplayText !== null
                ? props.currentRankPercentileDisplayText
                : "-"}
            </MUITypography>
          </MUIBox>
          <MUIBox
            sx={{
              alignItems: "baseline",
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <MUITypography
              sx={{
                color: (theme) => theme.palette.text.secondary,
                fontSize: "0.7rem",
                fontWeight: 500,
                marginBottom: 0.35,
              }}
            >
              Points until next rank
            </MUITypography>
            <MUITypography
              sx={{
                color: (theme) => theme.palette.text.primary,
                fontSize: "0.85rem",
                fontVariantNumeric: "tabular-nums",
                fontWeight: 700,
              }}
            >
              {props.pointsUntilNextRankDisplayText}
            </MUITypography>
          </MUIBox>
        </MUIBox>
      </MUIBox>
    </MUIBox>
  );
}

/**
 * Tooltip title element built from loaded widget payload (same fields as the badge).
 *
 * @param {object|null|undefined} widgetData
 * @returns {React.ReactElement}
 */
function buildPresidentsClubStandingTooltipTitleElement(
  widgetData,
  currentUserId,
) {
  const presidentsClubUserRecord = widgetData?.users?.[currentUserId] ?? null;
  const currentRankNumberDisplayText =
    presidentsClubUserRecord?.current?.rank !== null &&
    presidentsClubUserRecord?.current?.rank !== undefined
      ? String(presidentsClubUserRecord.current.rank)
      : "";

  const currentPercentile = presidentsClubUserRecord?.current?.percentile ?? 0;
  const topPercentValue = 100 - currentPercentile;
  const currentRankPercentileDisplayText = `${topPercentValue}%`;
  const pointsUntilNextRankDisplayText = buildPointsUntilNextRankDisplayText(
    presidentsClubUserRecord?.current?.nextRankDelta,
  );

  const currentRankAppearanceType =
    resolvePresidentsClubBadgeAppearanceKindFromCurrentRank(
      presidentsClubUserRecord?.current?.rank,
    );

  return (
    <PresidentsClubStandingTooltipPanel
      currentRankNumberDisplayText={currentRankNumberDisplayText}
      currentRankPercentileDisplayText={currentRankPercentileDisplayText}
      pointsUntilNextRankDisplayText={pointsUntilNextRankDisplayText}
      currentRankAppearanceType={currentRankAppearanceType}
    />
  );
}

/**
 * @param {object} props
 * @param {object|null|undefined} props.widgetData Loaded widget response data.
 * @returns {React.ReactElement}
 */
function PresidentsClubBadgeVisual(props) {
  const sanitizedReactIdentifierFragment = useId().replace(/:/g, "");
  const badgeFaceGradientIdentifier = `presidentsClubBadgeFaceGradient-${sanitizedReactIdentifierFragment}`;
  const badgeRimGradientIdentifier = `presidentsClubBadgeRimGradient-${sanitizedReactIdentifierFragment}`;
  const badgeSparkleClipPathIdentifier = `presidentsClubBadgeSparkleClip-${sanitizedReactIdentifierFragment}`;

  const presidentsClubUserRecord =
    props.widgetData?.users?.[props.currentUserId] ?? null;

  const currentRank =
    presidentsClubUserRecord?.current?.rank !== null &&
    presidentsClubUserRecord?.current?.rank !== undefined
      ? String(presidentsClubUserRecord.current.rank)
      : "";

  if (!currentRank) {
    return null;
  }

  const currentRankAppearanceType =
    resolvePresidentsClubBadgeAppearanceKindFromCurrentRank(
      presidentsClubUserRecord?.current?.rank,
    );

  const characterCount = currentRank.length;
  const centerFontSize =
    characterCount <= 2
      ? 24
      : characterCount === 3
        ? 17
        : characterCount === 4
          ? 14
          : 12;

  const presidentsClubBadgeAppearanceSettingsByKind = {
    gold: {
      badgeBackFillColor: "#b45309",
      badgeBevelFillColor: "#fde68a",
      badgeFaceHighlightColor: "#facc15",
      badgeFaceShadowColor: "#ca8a04",
      badgeRimHighlightColor: "#fef08a",
      badgeRimShadowColor: "#92400e",
      badgeStrokeColor: "#713f12",
      centerTextFillColor: "#713f12",
      labelTextFillColor: "#facc15",
      ribbonFillColor: "#dc2626",
      ribbonShadowColor: "#991b1b",
    },
    silver: {
      badgeBackFillColor: "#475569",
      badgeBevelFillColor: "#e2e8f0",
      badgeFaceHighlightColor: "#cbd5e1",
      badgeFaceShadowColor: "#64748b",
      badgeRimHighlightColor: "#f8fafc",
      badgeRimShadowColor: "#475569",
      badgeStrokeColor: "#334155",
      centerTextFillColor: "#1e293b",
      labelTextFillColor: "#cbd5e1",
      ribbonFillColor: "#3b82f6",
      ribbonShadowColor: "#1d4ed8",
    },
    bronze: {
      badgeBackFillColor: "#7c2d12",
      badgeBevelFillColor: "#fdba74",
      badgeFaceHighlightColor: "#b45309",
      badgeFaceShadowColor: "#7c2d12",
      badgeRimHighlightColor: "#fcd34d",
      badgeRimShadowColor: "#92400e",
      badgeStrokeColor: "#431407",
      centerTextFillColor: "#fff7ed",
      labelTextFillColor: "#fdba74",
      ribbonFillColor: "#f59e0b",
      ribbonShadowColor: "#92400e",
    },
    generic: {
      badgeBackFillColor: "#1d4ed8",
      badgeBevelFillColor: "#bfdbfe",
      badgeFaceHighlightColor: "#64748b",
      badgeFaceShadowColor: "#334155",
      badgeRimHighlightColor: "#93c5fd",
      badgeRimShadowColor: "#1e3a8a",
      badgeStrokeColor: "#0f172a",
      centerTextFillColor: "#eff6ff",
      labelTextFillColor: "#bfdbfe",
      ribbonFillColor: "#3b82f6",
      ribbonShadowColor: "#1e40af",
    },
  };

  const presidentsClubBadgeAppearanceSettings =
    presidentsClubBadgeAppearanceSettingsByKind[currentRankAppearanceType];

  const isCurrentRankBadgeSparkleEligible =
    currentRankAppearanceType === "gold" ||
    currentRankAppearanceType === "silver" ||
    currentRankAppearanceType === "bronze";

  let presidentsClubBadgeSparkleFillColor = "#fffef5";
  let presidentsClubBadgeShimmerFillColor = "#ffffff";
  if (currentRankAppearanceType === "silver") {
    presidentsClubBadgeSparkleFillColor = "#f8fafc";
    presidentsClubBadgeShimmerFillColor = "#e2e8f0";
  } else if (currentRankAppearanceType === "bronze") {
    presidentsClubBadgeSparkleFillColor = "#fff7ed";
    presidentsClubBadgeShimmerFillColor = "#fed7aa";
  }

  const presidentsClubBadgeSparkleGlyphPathDefinition =
    "M 0 -4.8 L 0.75 -0.75 L 4.8 0 L 0.75 0.75 L 0 4.8 L -0.75 0.75 L -4.8 0 L -0.75 -0.75 Z";

  const presidentsClubBadgeSparkleDisplayConfigurations =
    isCurrentRankBadgeSparkleEligible
      ? [
          {
            animationBeginSeconds: 0,
            animationDurationSeconds: 2.4,
            centerX: 30,
            centerY: 27,
            rotationDegrees: 18,
            scale: 0.48,
          },
          {
            animationBeginSeconds: 0.35,
            animationDurationSeconds: 2.9,
            centerX: 73,
            centerY: 20,
            rotationDegrees: -12,
            scale: 0.42,
          },
          {
            animationBeginSeconds: 0.8,
            animationDurationSeconds: 2.2,
            centerX: 69,
            centerY: 55,
            rotationDegrees: 34,
            scale: 0.34,
          },
          {
            animationBeginSeconds: 1.15,
            animationDurationSeconds: 2.7,
            centerX: 38,
            centerY: 61,
            rotationDegrees: -28,
            scale: 0.3,
          },
          {
            animationBeginSeconds: 1.55,
            animationDurationSeconds: 3.1,
            centerX: 53,
            centerY: 13,
            rotationDegrees: 8,
            scale: 0.26,
          },
        ]
      : [];

  return (
    <MUIBox
      component="span"
      role="img"
      sx={{
        alignItems: "flex-start",
        display: "flex",
        height: "100%",
        justifyContent: "flex-end",
        marginRight: (theme) => `-${theme.spacing(1)}`,
        marginTop: 0,
        minHeight: 0,
        paddingTop: 0,
        width: "100%",
        "--presidents-club-badge-back-fill-color": (theme) =>
          theme.palette.mode === "light" &&
          currentRankAppearanceType === "generic"
            ? "#dbeafe"
            : presidentsClubBadgeAppearanceSettings.badgeBackFillColor,
        "--presidents-club-badge-bevel-fill-color": (theme) =>
          theme.palette.mode === "light" &&
          currentRankAppearanceType === "generic"
            ? "#bfdbfe"
            : presidentsClubBadgeAppearanceSettings.badgeBevelFillColor,
        "--presidents-club-badge-face-highlight-color": (theme) =>
          theme.palette.mode === "light" &&
          currentRankAppearanceType === "generic"
            ? "#ffffff"
            : presidentsClubBadgeAppearanceSettings.badgeFaceHighlightColor,
        "--presidents-club-badge-face-shadow-color": (theme) =>
          theme.palette.mode === "light" &&
          currentRankAppearanceType === "generic"
            ? "#93c5fd"
            : presidentsClubBadgeAppearanceSettings.badgeFaceShadowColor,
        "--presidents-club-badge-rank-text-fill-color": (theme) =>
          theme.palette.mode === "light" &&
          currentRankAppearanceType === "generic"
            ? "#1e3a8a"
            : presidentsClubBadgeAppearanceSettings.centerTextFillColor,
        "--presidents-club-badge-rim-highlight-color": (theme) =>
          theme.palette.mode === "light" &&
          currentRankAppearanceType === "generic"
            ? "#eff6ff"
            : presidentsClubBadgeAppearanceSettings.badgeRimHighlightColor,
        "--presidents-club-badge-rim-shadow-color": (theme) =>
          theme.palette.mode === "light" &&
          currentRankAppearanceType === "generic"
            ? "#60a5fa"
            : presidentsClubBadgeAppearanceSettings.badgeRimShadowColor,
        "--presidents-club-badge-stroke-color": (theme) =>
          theme.palette.mode === "light" &&
          currentRankAppearanceType === "generic"
            ? "#3b82f6"
            : presidentsClubBadgeAppearanceSettings.badgeStrokeColor,
      }}
    >
      <svg
        aria-hidden="true"
        fill="none"
        height="70"
        viewBox="0 -6 104 110"
        width="80"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient
            id={badgeFaceGradientIdentifier}
            x1="15%"
            x2="88%"
            y1="12%"
            y2="100%"
          >
            <stop
              offset="0%"
              stopColor="var(--presidents-club-badge-face-highlight-color)"
            />
            <stop
              offset="62%"
              stopColor="var(--presidents-club-badge-face-shadow-color)"
            />
            <stop
              offset="100%"
              stopColor="var(--presidents-club-badge-face-highlight-color)"
            />
          </linearGradient>
          <linearGradient
            id={badgeRimGradientIdentifier}
            x1="0%"
            x2="100%"
            y1="0%"
            y2="100%"
          >
            <stop
              offset="0%"
              stopColor="var(--presidents-club-badge-rim-highlight-color)"
            />
            <stop
              offset="50%"
              stopColor="var(--presidents-club-badge-rim-shadow-color)"
            />
            <stop
              offset="100%"
              stopColor="var(--presidents-club-badge-rim-highlight-color)"
            />
          </linearGradient>
          {isCurrentRankBadgeSparkleEligible === true ? (
            <clipPath id={badgeSparkleClipPathIdentifier}>
              <polygon points="52 3 80 19 80 57 52 76 24 57 24 19" />
            </clipPath>
          ) : null}
        </defs>

        <path
          d="M 26 55 L 46 66 L 46 88 L 36 82 L 26 86 Z"
          fill={presidentsClubBadgeAppearanceSettings.ribbonShadowColor}
        />
        <path
          d="M 78 55 L 58 66 L 58 88 L 68 82 L 78 86 Z"
          fill={presidentsClubBadgeAppearanceSettings.ribbonFillColor}
        />
        <path
          d="M 26 55 L 46 66 L 46 88 L 36 82 L 26 86 Z"
          fill={presidentsClubBadgeAppearanceSettings.ribbonFillColor}
          opacity="0.82"
        />

        <polygon
          fill="var(--presidents-club-badge-back-fill-color)"
          points="52 3 80 19 80 57 52 76 24 57 24 19"
          stroke="var(--presidents-club-badge-stroke-color)"
          strokeLinejoin="round"
          strokeWidth="2.5"
        />
        <polygon
          fill={`url(#${badgeRimGradientIdentifier})`}
          points="52 8 75 22 75 54 52 70 29 54 29 22"
          stroke="var(--presidents-club-badge-bevel-fill-color)"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <polygon
          fill={`url(#${badgeFaceGradientIdentifier})`}
          points="52 15 69 25 69 51 52 62 35 51 35 25"
        />
        <path
          d="M 35 25 L 52 15 L 69 25 L 63 31 L 52 24 L 41 31 Z"
          fill="#ffffff"
          opacity="0.2"
        />
        <path
          d="M 69 25 L 69 51 L 52 62 L 54 53 L 63 47 L 63 31 Z"
          fill="#000000"
          opacity="0.12"
        />
        <path
          d="M 35 51 L 52 62 L 54 53 L 41 47 L 41 31 L 35 25 Z"
          fill="#ffffff"
          opacity="0.1"
        />
        {isCurrentRankBadgeSparkleEligible === true ? (
          <g clipPath={`url(#${badgeSparkleClipPathIdentifier})`}>
            <path
              d="M 12 9 L 22 3 L 90 70 L 80 76 Z"
              fill={presidentsClubBadgeShimmerFillColor}
              opacity="0"
            >
              <animate
                attributeName="opacity"
                dur="3.6s"
                keyTimes="0;0.18;0.36;1"
                repeatCount="indefinite"
                values="0;0.32;0;0"
              />
              <animateTransform
                attributeName="transform"
                dur="3.6s"
                from="-50 0"
                repeatCount="indefinite"
                to="58 0"
                type="translate"
              />
            </path>
            {presidentsClubBadgeSparkleDisplayConfigurations.map(
              (
                presidentsClubBadgeSparkleDisplayConfiguration,
                presidentsClubBadgeSparkleDisplayConfigurationIndex,
              ) => (
                <g
                  key={`presidentsClubBadgeSparkle-${presidentsClubBadgeSparkleDisplayConfigurationIndex}-${presidentsClubBadgeSparkleDisplayConfiguration.centerX}-${presidentsClubBadgeSparkleDisplayConfiguration.centerY}`}
                  transform={`translate(${presidentsClubBadgeSparkleDisplayConfiguration.centerX} ${presidentsClubBadgeSparkleDisplayConfiguration.centerY}) rotate(${presidentsClubBadgeSparkleDisplayConfiguration.rotationDegrees}) scale(${presidentsClubBadgeSparkleDisplayConfiguration.scale})`}
                >
                  <path
                    d={presidentsClubBadgeSparkleGlyphPathDefinition}
                    fill={presidentsClubBadgeSparkleFillColor}
                    opacity="0"
                  >
                    <animate
                      attributeName="opacity"
                      begin={`${presidentsClubBadgeSparkleDisplayConfiguration.animationBeginSeconds}s`}
                      dur={`${presidentsClubBadgeSparkleDisplayConfiguration.animationDurationSeconds}s`}
                      keyTimes="0;0.16;0.34;0.58;0.82;1"
                      repeatCount="indefinite"
                      values="0;1;0.18;0.86;0.1;0"
                    />
                  </path>
                </g>
              ),
            )}
          </g>
        ) : null}
        <text
          dominantBaseline="middle"
          fill="var(--presidents-club-badge-rank-text-fill-color)"
          fontSize={centerFontSize}
          fontWeight={700}
          textAnchor="middle"
          x="51"
          y="41"
        >
          {currentRank}
        </text>
      </svg>
    </MUIBox>
  );
}

/**
 * Queue dashboard widget: Presidents Club standing with a badge showing rank (place).
 *
 * @param {object} props
 * @param {number|string} props.widgetId Dashboard widget instance identifier used to load data.
 * @param {object} props.dashboardWidget Dashboard widget configuration including display name.
 * @param {boolean} props.isActive Whether this tile is the active dashboard widget.
 * @returns {React.ReactElement}
 */
export default function WidgetDashboardPresidentsClub(props) {
  const mainContext = useContext(MainContext);
  const currentUserId = String(mainContext.currentUser.id);

  return (
    <Widget
      getWidgetId={() => props.widgetId}
      getTitle={() => props.dashboardWidget.name}
      isActive={() => props.isActive}
      renderTooltip={({ widgetData, isLoadingWidgetData }) => {
        if (isLoadingWidgetData === true) {
          return null;
        }
        return buildPresidentsClubStandingTooltipTitleElement(
          widgetData,
          currentUserId,
        );
      }}
      getSubtitle={({ widgetData }) => {
        if (widgetData === null || widgetData === undefined) {
          return null;
        }
        const rankChangeThisWeek =
          widgetData?.users?.[currentUserId]?.change?.rank;
        if (rankChangeThisWeek === null || rankChangeThisWeek === undefined) {
          return null;
        }

        if (rankChangeThisWeek === 0) {
          return "No change in rank this week";
        }

        const absoluteDifference = Math.abs(rankChangeThisWeek);
        const formattedDifference = Format.number(absoluteDifference, {
          decimalPlaceCount: 0,
          shouldRoundValueToDecimalPlacesBeforeFormatting: true,
        });

        if (rankChangeThisWeek < 0) {
          return (
            <MUIBox
              component="span"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <MUIBox component="span" color="success.main">
                <MUIIconArrowDropUp fontSize="large" />
              </MUIBox>
              <MUIBox component="span">
                Climbed {formattedDifference} places this week
              </MUIBox>
            </MUIBox>
          );
        } else {
          return (
            <MUIBox
              component="span"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <MUIBox component="span" color="error.main">
                <MUIIconArrowDropDown fontSize="large" />
              </MUIBox>
              <MUIBox component="span">
                Dropped {formattedDifference} places this week
              </MUIBox>
            </MUIBox>
          );
        }
      }}
      renderContent={({ widgetData }) => {
        const currentPoints =
          widgetData?.users?.[currentUserId]?.current?.points ?? 0;
        return (
          <MUIBox
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "baseline",
              gap: 0.5,
              pr: 0.5,
            }}
          >
            <MUITypography component="div" variant="h5">
              {Format.number(currentPoints, {
                decimalPlaceCount: 2,
                shouldRoundValueToDecimalPlacesBeforeFormatting: true,
              })}
            </MUITypography>
            <MUITypography
              component="span"
              color="text.secondary"
              variant="body2"
            >
              points
            </MUITypography>
          </MUIBox>
        );
      }}
      renderVisual={({ widgetData }) => (
        <MUIBox
          sx={{
            alignItems: "flex-start",
            display: "flex",
            height: "100%",
            justifyContent: "flex-end",
            minHeight: 0,
            width: "100%",
          }}
        >
          <PresidentsClubBadgeVisual
            currentUserId={currentUserId}
            widgetData={widgetData}
          />
        </MUIBox>
      )}
    />
  );
}
